import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  TextField,
  Box,
  Typography,
  List,
  ListItemButton,
  ListSubheader,
  Chip,
  InputAdornment,
  Skeleton,
  CircularProgress,
  Fade,
  FormControlLabel,
  Switch,
  Tooltip,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as OrderIcon,
  Person as PatientIcon,
  Autorenew as SubscriptionIcon,
  Public as PublicIcon,
  Storefront as PharmacyIcon,
  Lock as LockIcon,
  Home as HomeIcon,
  Medication as MedicationIcon,
  EventAvailable as EventAvailableIcon,
  History as HistoryIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { useAuth } from '../../hooks/useAuth';
import { useBlockHidden } from '../../hooks/queries/usePharmacyVisibility';
import { useGlobalSearch } from './useGlobalSearch';
import { pmsDisplayName } from '../../utils/pmsDisplayName';
import { GroupPatientChip } from '../common/GroupPatientChip';
import { PatientStatusChip } from '../common/PatientStatusChip';
import {
  useRecentGlobalSearchesStore,
  RecentGlobalSearchEntry,
} from '../../stores/recentGlobalSearchesStore';
import { SearchResultColumn } from './SearchResultColumn';

interface GlobalSearchPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface FlatItem {
  type: 'order' | 'patient' | 'subscription';
  data: any;
}

const stageLabels: Record<string, string> = {
  billing: 'Billing',
  clinical_verification: 'Clinical',
  packaging: 'Packaging',
  product_verification: 'Product Verify',
  ready_to_ship: 'Ready to Ship',
  staging: 'Staging',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  ready_for_pickup: 'Ready for Pickup',
  picked_up: 'Picked Up',
  delivery_missed: 'Missed',
};

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * Stable color per GSP pharmacy so the routing badge is instantly readable
 * at a glance — PAs see a green chip and know "London" without reading.
 * Keyed by the 5 GSP pharmacy IDs (matches the backend allowlist).
 */
const PHARMACY_BADGE_COLOR: Record<string, string> = {
  '64662eb695bfef1e99ffbed6': '#15803d', // London — green
  '645b9c128158f06a385d5043': '#0f766e', // Mississauga — teal
  '64662e7c95bfef1e99ffbec9': '#1d4ed8', // Ottawa — blue
  '641b4aac2d2521c964108378': '#6d28d9', // Winnipeg — purple
  '64876b9ea40f72e6937196a9': '#c2410c', // Burnaby — orange
};

interface PharmacyBadgeProps {
  pharmacyId?: string;
  pharmacyName?: string | null;
}

const PharmacyBadge: React.FC<PharmacyBadgeProps> = ({ pharmacyId, pharmacyName }) => {
  if (!pharmacyName) return null;
  const color = pharmacyId ? PHARMACY_BADGE_COLOR[pharmacyId] ?? '#475569' : '#475569';
  return (
    <Tooltip title={pharmacyName} arrow>
      <Chip
        icon={<PharmacyIcon sx={{ fontSize: 13, color: '#fff !important' }} />}
        label={pharmacyName}
        size="small"
        sx={{
          height: 22,
          fontSize: 11.5,
          fontWeight: 600,
          color: '#fff',
          bgcolor: color,
          borderRadius: 1,
          '& .MuiChip-label': { px: 0.75 },
          '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
        }}
      />
    </Tooltip>
  );
};

export function GlobalSearchPalette({ open, onClose }: GlobalSearchPaletteProps) {
  const theme = useTheme();
  // PRX-57: below `md` the two-column results grid collapses to one
  // stacked column (Patients, Orders, Subscriptions) — the same breakpoint
  // the grid's `gridTemplateColumns` switches on below, so the flag and
  // the layout never disagree.
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const pharmacyId = usePharmacyStore((s) => s.selectedPharmacyId);
  // PRX-64: the "checking <PMS>…" row and its "from <PMS>" resolution suffix
  // name the active pharmacy's own system — Fillware at a Fillware pharmacy,
  // Kroll everywhere else (see pmsDisplayName).
  const activePharmacy = usePharmacyStore((s) => s.activePharmacy);
  const pmsName = pmsDisplayName(activePharmacy?.databaseType);
  const { enqueueSnackbar } = useSnackbar();
  const { hasCustomPermission, customRole } = useAuth();
  // Permission gate: anyone whose role grants the cross-pharmacy carveout
  // sees the toggle. When customRole hasn't loaded yet (graceful fallback per
  // CLAUDE.md), the toggle is shown — backend still enforces.
  // PRX-196: hidden by default at a Fillware pharmacy (PharmAssist is one
  // pharmacy, not part of the GSP network). Hidden means no switch AND no
  // identifier query broadening to every pharmacy on its own.
  const crossPharmacySwitchHidden = useBlockHidden('topbar.search.all_pharmacies');
  const canSearchAcrossPharmacies =
    !crossPharmacySwitchHidden && (!customRole || hasCustomPermission('canSearchAcrossPharmacies'));

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [crossPharmacyToggle, setCrossPharmacy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // PRX-57: now wraps the two-column results grid (a Box/div), not a
  // single <List> — still the scroll-into-view anchor for the selected row.
  const listRef = useRef<HTMLDivElement>(null);

  const recentEntries = useRecentGlobalSearchesStore((s) => s.entries);
  const recordRecent = useRecentGlobalSearchesStore((s) => s.record);
  const clearRecent = useRecentGlobalSearchesStore((s) => s.clear);

  const {
    results,
    isLoading,
    error,
    reset,
    krollFallbackLoading,
    krollFallbackActive,
    // Effective value: true if the user flipped the toggle OR an identifier-
    // like query auto-broadened the request. Drives the per-row PharmacyBadge
    // so it shows whenever the universal aggregator was actually hit.
    crossPharmacy,
    pmsRx,
  } = useGlobalSearch(pharmacyId, query, {
    crossPharmacy: canSearchAcrossPharmacies && crossPharmacyToggle,
    // Identifier-like queries (ORD-…, Rx numbers, phone numbers) auto-
    // broaden to the cross-pharmacy aggregator so staff can always find
    // a patient by a unique number regardless of which pharmacy owns
    // them. Gated on permission to avoid wasted requests.
    autoBroadenIdentifierQueries: canSearchAcrossPharmacies,
  });

  // Flatten results for keyboard navigation — Patients first (left column),
  // then Orders, then Subscriptions (right column, PRX-57). This is still
  // the single source of truth for `selectedIndex`; only the order changed.
  // Up/Down walk the flat list (so it moves through the left column then
  // the right); Left/Right (in handleKeyDown, below) derive a column and
  // row offset from the index to jump to the same offset in the other
  // column.
  const flatItems = useMemo<FlatItem[]>(() => {
    const items: FlatItem[] = [];
    results.patients.forEach((p) => items.push({ type: 'patient', data: p }));
    results.orders.forEach((o) => items.push({ type: 'order', data: o }));
    results.subscriptions.forEach((s) => items.push({ type: 'subscription', data: s }));
    return items;
  }, [results]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      reset();
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, reset]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [flatItems.length]);

  useEffect(() => {
    if (!listRef.current) return;
    const selected = listRef.current.querySelector('[data-selected="true"]');
    selected?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  function recordItem(item: FlatItem) {
    const d = item.data;
    if (item.type === 'order') {
      recordRecent({
        key: `order:${d.id}`,
        type: 'order',
        id: d.id,
        pharmacyId: d.pharmacyId,
        pharmacyName: d.pharmacyName,
        orderNumber: d.orderNumber,
        patientName: d.patientName,
        rxNumber: d.rxNumber,
        workflowStage: d.workflowStage,
      });
    } else if (item.type === 'patient') {
      recordRecent({
        key: `patient:${d.compositeId ?? d.id}`,
        type: 'patient',
        id: d.compositeId ?? d.id,
        pharmacyId: d.pharmacyId,
        pharmacyName: d.pharmacyName,
        firstName: d.firstName,
        lastName: d.lastName,
        dateOfBirth: d.dateOfBirth,
      });
    } else {
      recordRecent({
        key: `subscription:${d.id}`,
        type: 'subscription',
        id: d.id,
        pharmacyId: d.pharmacyId,
        pharmacyName: d.pharmacyName,
        rxNumber: d.rxNumber,
        medicationName: d.medicationName,
        patientName: d.patientName,
      });
    }
  }

  function navigateToItem(item: FlatItem) {
    // Cross-pharmacy carve-out (GP-314): when a result belongs to a pharmacy
    // OTHER than the user's currently selected one, we don't navigate. The
    // workflow is "identify the right pharmacy, then verbally transfer the
    // caller" — opening the order in the wrong pharmacy context would just
    // confuse downstream queues and lock state. Surface a routing toast so
    // the PA has a clear visual confirmation of where to forward the call.
    const itemPharmacyId: string | undefined = item.data.pharmacyId;
    const isDifferentPharmacy =
      !!itemPharmacyId && !!pharmacyId && itemPharmacyId !== pharmacyId;

    // Record before navigating — covers both same-pharmacy opens and
    // cross-pharmacy transfer-toasts so users can refind either.
    recordItem(item);

    if (isDifferentPharmacy) {
      const pharmacyLabel = item.data.pharmacyName;
      const patientLabel =
        item.type === 'patient'
          ? `${item.data.lastName}, ${item.data.firstName}`
          : item.data.patientName;
      enqueueSnackbar(`Transfer ${patientLabel} to ${pharmacyLabel}`, {
        variant: 'info',
        autoHideDuration: 6000,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      onClose();
      return;
    }

    onClose();
    // Same pharmacy — open the record in the current context.
    const targetPharmacyId = itemPharmacyId || pharmacyId;
    switch (item.type) {
      case 'order':
        navigate(`/orders/${targetPharmacyId}/${item.data.id}`);
        break;
      case 'patient':
        navigate(`/patients/${item.data.compositeId}`);
        break;
      case 'subscription':
        navigate(
          // GP-1204: the queue lives at /billing-queue — /billing is an
          // unregistered path and rendered a blank page.
          `/order-fulfillment/billing-queue?search=${encodeURIComponent(item.data.rxNumber || item.data.patientName || '')}`,
        );
        break;
    }
  }

  function handleRecentClick(entry: RecentGlobalSearchEntry) {
    // Rehydrate as a FlatItem so we reuse the same routing/transfer logic.
    if (entry.type === 'order') {
      navigateToItem({
        type: 'order',
        data: {
          id: entry.id,
          pharmacyId: entry.pharmacyId,
          pharmacyName: entry.pharmacyName,
          orderNumber: entry.orderNumber,
          patientName: entry.patientName,
          rxNumber: entry.rxNumber,
          workflowStage: entry.workflowStage,
        },
      });
    } else if (entry.type === 'patient') {
      navigateToItem({
        type: 'patient',
        data: {
          id: entry.id,
          compositeId: entry.id,
          pharmacyId: entry.pharmacyId,
          pharmacyName: entry.pharmacyName,
          firstName: entry.firstName,
          lastName: entry.lastName,
          dateOfBirth: entry.dateOfBirth,
        },
      });
    } else {
      navigateToItem({
        type: 'subscription',
        data: {
          id: entry.id,
          pharmacyId: entry.pharmacyId,
          pharmacyName: entry.pharmacyName,
          rxNumber: entry.rxNumber,
          medicationName: entry.medicationName,
          patientName: entry.patientName,
        },
      });
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'ArrowRight' && !isNarrow) {
      // PRX-57: jump from the highlighted patient (left column) to the
      // same row offset on the right (orders, then subscriptions). A
      // no-op when there's no left column, no right column, or the
      // cursor is already on the right — narrow view has one column, so
      // Left/Right do nothing there.
      e.preventDefault();
      setSelectedIndex((i) => {
        const patientsCount = results.patients.length;
        const rightCount = results.orders.length + results.subscriptions.length;
        if (patientsCount === 0 || rightCount === 0 || i >= patientsCount) return i;
        return patientsCount + Math.min(i, rightCount - 1);
      });
    } else if (e.key === 'ArrowLeft' && !isNarrow) {
      // Mirror of ArrowRight: jump from the right column back to the same
      // row offset among patients, clamped to the last patient row.
      e.preventDefault();
      setSelectedIndex((i) => {
        const patientsCount = results.patients.length;
        if (patientsCount === 0 || i < patientsCount) return i;
        return Math.min(i - patientsCount, patientsCount - 1);
      });
    } else if (e.key === 'Enter' && flatItems[selectedIndex]) {
      e.preventDefault();
      navigateToItem(flatItems[selectedIndex]);
    }
  }

  const hasResults = flatItems.length > 0;
  // Don't flash "No results in PHOX" while the Kroll fallback is still checking —
  // it would show, then get replaced by a patient a moment later.
  const showEmpty =
    query.length >= 2 && !isLoading && !krollFallbackLoading && !hasResults && !error;

  let flatIdx = 0;

  const accent = crossPharmacy ? theme.palette.warning.main : theme.palette.primary.main;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '8%',
          m: 0,
          borderRadius: 3,
          maxHeight: '78vh',
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.25)',
          borderTop: `3px solid ${accent}`,
          transition: 'border-top-color 0.2s ease',
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: alpha('#020617', 0.55), backdropFilter: 'blur(6px)' } },
      }}
    >
      {/* Header: input + mode toggle */}
      <Box sx={{ p: 2.5, pb: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          variant="outlined"
          placeholder={
            crossPharmacy
              ? 'Search every GSP pharmacy by patient name or phone…'
              : 'Search orders, patients, subscriptions…'
          }
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          slotProps={{
            input: {
              sx: { fontSize: 18, py: 0.5 },
              startAdornment: (
                <InputAdornment position="start">
                  {crossPharmacy ? (
                    <PublicIcon sx={{ color: accent, fontSize: 26 }} />
                  ) : (
                    <SearchIcon sx={{ color: 'action.active', fontSize: 26 }} />
                  )}
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Chip
                    label="ESC"
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                  />
                </InputAdornment>
              ),
            },
          }}
        />

        {canSearchAcrossPharmacies && (
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={crossPharmacyToggle}
                  onChange={(e) => setCrossPharmacy(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <PublicIcon sx={{ fontSize: 16, color: crossPharmacy ? accent : 'action.active' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: crossPharmacy ? accent : 'text.primary',
                    }}
                  >
                    Search all GSP pharmacies
                  </Typography>
                </Box>
              }
              sx={{ mr: 0 }}
            />
            {crossPharmacy ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LockIcon sx={{ fontSize: 13, color: accent }} />
                <Typography variant="caption" sx={{ color: accent, fontWeight: 600 }}>
                  Audit-logged
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Dates shown as DD/MM/YYYY · ↑↓ to navigate, Enter to open
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ overflow: 'auto', maxHeight: 'calc(78vh - 160px)', py: 1 }}>
        {query.length < 2 && recentEntries.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body2" color="text.secondary">
              Type at least 2 characters to search
            </Typography>
            {crossPharmacy && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Results span London, Mississauga, Ottawa, Winnipeg, Burnaby
              </Typography>
            )}
          </Box>
        )}

        {query.length < 2 && recentEntries.length > 0 && (
          <Box sx={{ px: 2.5, pt: 1, pb: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 0.75,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    color: 'text.secondary',
                  }}
                >
                  Recently viewed · {recentEntries.length}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                onClick={clearRecent}
                sx={{
                  cursor: 'pointer',
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main', textDecoration: 'underline' },
                }}
              >
                Clear
              </Typography>
            </Box>
            <List dense disablePadding>
              {recentEntries.map((entry) => {
                const Icon =
                  entry.type === 'order'
                    ? OrderIcon
                    : entry.type === 'patient'
                      ? PatientIcon
                      : SubscriptionIcon;

                const primary =
                  entry.type === 'order'
                    ? entry.orderNumber
                    : entry.type === 'patient'
                      ? `${entry.lastName}, ${entry.firstName}`
                      : entry.rxNumber
                        ? `Rx ${entry.rxNumber}`
                        : entry.medicationName || 'Subscription';

                const secondary =
                  entry.type === 'order'
                    ? entry.patientName
                    : entry.type === 'patient'
                      ? entry.dateOfBirth
                        ? `DOB ${formatDate(entry.dateOfBirth)}`
                        : ''
                      : `${entry.medicationName || ''}${
                          entry.patientName ? ` · ${entry.patientName}` : ''
                        }`;

                return (
                  <ListItemButton
                    key={entry.key}
                    onClick={() => handleRecentClick(entry)}
                    sx={{
                      mx: 0,
                      my: 0.25,
                      borderRadius: 1.5,
                      py: 1,
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        width: '100%',
                        minWidth: 0,
                      }}
                    >
                      <Icon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        noWrap
                        sx={{ minWidth: 170 }}
                      >
                        {primary}
                      </Typography>
                      {secondary && (
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ minWidth: 130, color: 'text.primary' }}
                        >
                          {secondary}
                        </Typography>
                      )}
                      <Box sx={{ flex: 1 }} />
                      {crossPharmacy && (
                        <PharmacyBadge
                          pharmacyId={entry.pharmacyId}
                          pharmacyName={entry.pharmacyName}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {timeAgo(entry.viewedAt)}
                      </Typography>
                      <LaunchIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        )}

        {isLoading && query.length >= 2 && (
          <Box sx={{ px: 2.5, py: 1 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={44} sx={{ my: 0.5, borderRadius: 1 }} />
            ))}
          </Box>
        )}

        {krollFallbackLoading && !isLoading && query.length >= 2 && (
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Not in PHOX — checking {pmsName}…
            </Typography>
          </Box>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 4 }}>
            {error}
          </Typography>
        )}

        {showEmpty && (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              No results in PHOX
            </Typography>
            {crossPharmacy && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Not in PHOX — try Kroll for retail walk-ins
              </Typography>
            )}
          </Box>
        )}

        {hasResults && !isLoading && (
          <Fade in>
            {/* PRX-57 review: this Box carries the SAME height budget the
                outer body Box uses (calc(78vh - 160px)) and is itself
                non-scrolling — a flex column so the (optional, fixed-
                height) PMS-Rx row above keeps its natural size and the
                grid below gets the remainder via `flex: 1, minHeight: 0`.
                That remainder is what each column's own `overflowY: auto`
                scrolls, independently of the other column, instead of the
                two columns scrolling in lockstep via one shared box. */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'calc(78vh - 160px)',
                overflow: 'hidden',
              }}
            >
              {/* PRX-15: the Rx number as the pharmacy system knows it — even
                  before PHOX has an order for it. Replaces the separate
                  "Search Rx" dialog on the top bar. Full-width, above the
                  two-column grid — not part of the column layout or the
                  flat keyboard index. */}
              {pmsRx && (
                <List dense disablePadding sx={{ mb: 0.5, flexShrink: 0 }}>
                  <ListSubheader
                    sx={{
                      bgcolor: 'transparent',
                      lineHeight: '34px',
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      color: 'text.secondary',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <MedicationIcon sx={{ fontSize: 16 }} />
                      Prescription · Rx {pmsRx.rxNumber}
                    </Box>
                  </ListSubheader>
                  <ListItemButton
                    data-testid="global-search-pms-rx"
                    onClick={() => {
                      onClose();
                      navigate(`/patients/${pmsRx.compositePatientId}`);
                    }}
                    sx={{ borderRadius: 1, mx: 1, mb: 0.5 }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {pmsRx.patientName} — {pmsRx.drugName}
                        {pmsRx.strength ? ` ${pmsRx.strength}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Open the patient's profile — prescriptions, hardcopy and images
                      </Typography>
                    </Box>
                  </ListItemButton>
                </List>
              )}

              {/* PRX-57: patients (left) and orders + subscriptions (right)
                  side by side at `md` and up; one stacked column, same
                  order (Patients, Orders, Subscriptions), below `md`. */}
              <Box
                ref={listRef}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isNarrow ? '1fr' : '1fr 1fr',
                  columnGap: 2,
                  flex: 1,
                  minHeight: 0,
                }}
              >
                {/* Patients — a top-level column, so it scrolls on its own
                    within the space the flex row above gives it. */}
                <SearchResultColumn
                  data-testid="global-search-column-patients"
                  sx={{ height: '100%', overflowY: 'auto' }}
                  icon={<PatientIcon sx={{ fontSize: 16 }} />}
                  label="Patients"
                  count={results.patients.length}
                  emptyText="No patients"
                  extra={
                    krollFallbackActive ? (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          ml: 0.75,
                          color: 'success.main',
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        • from {pmsName} (added to PHOX)
                      </Typography>
                    ) : null
                  }
                >
                  <List dense disablePadding>
                    {results.patients.map((patient) => {
                      const idx = flatIdx++;
                      // Prefer the address row that actually matched the search
                      // tokens over the patient's denormalized primary columns
                      // so the PA sees the line they typed against. Falls back
                      // to the primary address for patients found by name/phone.
                      const matched = patient.matchedAddress;
                      const addressParts = matched
                        ? [
                            matched.unitNumber ? `${matched.unitNumber}-` : '',
                            matched.addressLine1,
                            matched.city,
                            matched.province,
                            matched.postalCode,
                          ]
                        : [
                            patient.addressUnit ? `${patient.addressUnit}-` : '',
                            patient.addressStreet,
                            patient.addressCity,
                            patient.addressProvince,
                          ];
                      const addressLine = addressParts.filter(Boolean).join(' ').trim();
                      // Show the address row whenever we matched on address, OR
                      // in cross-pharmacy mode (where it's part of the routing
                      // disambiguation). Pharmacy-scoped mode with a non-address
                      // match keeps the original behaviour (no secondary row).
                      const showAddressRow = !!matched || crossPharmacy;
                      const isNonPrimaryMatch =
                        !!matched &&
                        matched.addressType !== 'home' &&
                        matched.addressType !== 'primary' &&
                        matched.addressType !== 'delivery';
                      const meds = patient.medications ?? [];
                      // '' for a value that is not a readable date: the row is
                      // hidden, never shown as "Last fill" with no date.
                      const lastFill = formatDate(patient.lastFillDate);
                      return (
                        <ListItemButton
                          key={patient.id}
                          data-selected={idx === selectedIndex}
                          selected={idx === selectedIndex}
                          onClick={() => navigateToItem({ type: 'patient', data: patient })}
                          sx={{
                            mx: 1.5,
                            my: 0.25,
                            borderRadius: 1.5,
                            py: 1,
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            '&.Mui-selected': {
                              bgcolor: alpha(accent, 0.08),
                              '&:hover': { bgcolor: alpha(accent, 0.12) },
                            },
                          }}
                        >
                          {/* Primary row: identity + status */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.25,
                              width: '100%',
                              minWidth: 0,
                            }}
                          >
                            <Typography variant="body2" fontWeight={700} noWrap sx={{ minWidth: 200 }}>
                              {patient.lastName}, {patient.firstName}
                            </Typography>
                            {patient.dateOfBirth && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ minWidth: 130, fontVariantNumeric: 'tabular-nums' }}
                              >
                                DOB {formatDate(patient.dateOfBirth)}
                              </Typography>
                            )}
                            {patient.phonePrimary && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {patient.phonePrimary}
                              </Typography>
                            )}
                            {/* GP-1348: WHICH nursing/group home, by name — four
                                patients who share a common name are told apart by
                                their home, not by DOB. Absent when they're in none. */}
                            <GroupPatientChip
                              group={patient.nhGroupName ? { groupName: patient.nhGroupName } : null}
                              maxWidth={160}
                            />
                            <Box sx={{ flex: 1 }} />
                            {crossPharmacy && (
                              <PharmacyBadge
                                pharmacyId={patient.pharmacyId}
                                pharmacyName={patient.pharmacyName}
                              />
                            )}
                            {/* PRX-36: the PHOX status, worded and coloured the
                                one way it is worded everywhere else. The chip it
                                replaced labelled a row with no status "active"
                                in success green — a fact nobody had recorded —
                                and showed a deceased patient a grey "deceased".
                                Nothing renders for active or unknown. */}
                            <PatientStatusChip status={patient.status} />
                          </Box>

                          {/* Secondary row: address (always when matched on address;
                              cross-pharmacy mode also shows last fill + meds). */}
                          {showAddressRow && (addressLine || lastFill || meds.length > 0) && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                mt: 0.5,
                                pl: 0,
                                flexWrap: 'wrap',
                                color: 'text.secondary',
                              }}
                            >
                              {addressLine && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                  <HomeIcon sx={{ fontSize: 13 }} />
                                  <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{ maxWidth: 280 }}
                                  >
                                    {addressLine}
                                  </Typography>
                                  {isNonPrimaryMatch && (
                                    <Typography
                                      variant="caption"
                                      sx={{ fontStyle: 'italic', opacity: 0.75 }}
                                    >
                                      ({matched!.addressType.replace(/_/g, ' ')})
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {lastFill && (
                                <Tooltip title="Most recent Rx fill date, from the pharmacy system" arrow>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <EventAvailableIcon sx={{ fontSize: 13 }} />
                                    <Typography
                                      variant="caption"
                                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                                    >
                                      Last fill {lastFill}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              )}
                              {meds.length > 0 && (
                                <Tooltip title={meds.join('\n')} arrow placement="top">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                    <MedicationIcon sx={{ fontSize: 13 }} />
                                    <Typography
                                      variant="caption"
                                      noWrap
                                      sx={{ maxWidth: 360 }}
                                    >
                                      {meds.slice(0, 3).join(', ')}
                                      {meds.length > 3 ? ` +${meds.length - 3} more` : ''}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </ListItemButton>
                      );
                    })}
                  </List>
                </SearchResultColumn>

                {/* Orders + Subscriptions (right column) */}
                <Box
                  data-testid="global-search-column-orders"
                  sx={{ minWidth: 0, height: '100%', overflowY: 'auto' }}
                >
                  <SearchResultColumn
                    icon={<OrderIcon sx={{ fontSize: 16 }} />}
                    label="Orders"
                    count={results.orders.length}
                    emptyText="No orders"
                  >
                    <List dense disablePadding>
                      {results.orders.map((order) => {
                        const idx = flatIdx++;
                        return (
                          <ListItemButton
                            key={order.id}
                            data-selected={idx === selectedIndex}
                            selected={idx === selectedIndex}
                            onClick={() => navigateToItem({ type: 'order', data: order })}
                            sx={{
                              mx: 1.5,
                              my: 0.25,
                              borderRadius: 1.5,
                              py: 1,
                              '&.Mui-selected': {
                                bgcolor: alpha(accent, 0.08),
                                '&:hover': { bgcolor: alpha(accent, 0.12) },
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                width: '100%',
                                minWidth: 0,
                              }}
                            >
                              <Typography variant="body2" fontWeight={700} noWrap sx={{ minWidth: 170 }}>
                                {order.orderNumber}
                              </Typography>
                              <Typography variant="body2" noWrap sx={{ minWidth: 130, color: 'text.primary' }}>
                                {order.patientName}
                              </Typography>
                              {order.rxNumber && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  Rx {order.rxNumber}
                                </Typography>
                              )}
                              <Box sx={{ flex: 1 }} />
                              {crossPharmacy && (
                                <PharmacyBadge
                                  pharmacyId={order.pharmacyId}
                                  pharmacyName={order.pharmacyName}
                                />
                              )}
                              <Chip
                                label={stageLabels[order.workflowStage] || order.workflowStage}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 22, fontSize: 11.5, fontWeight: 600 }}
                              />
                              {order.deliveryDate && (
                                <Tooltip title="Delivery date (DD/MM/YYYY)" arrow>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ minWidth: 130, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                                  >
                                    Deliv {formatDate(order.deliveryDate)}
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </SearchResultColumn>

                  {/* Subscriptions */}
                  {results.subscriptions.length > 0 && (
                    <SearchResultColumn
                      icon={<SubscriptionIcon sx={{ fontSize: 16 }} />}
                      label="Subscriptions"
                      count={results.subscriptions.length}
                      emptyText="No subscriptions"
                    >
                      <List dense disablePadding>
                        {results.subscriptions.map((sub) => {
                          const idx = flatIdx++;
                          return (
                            <ListItemButton
                              key={sub.id}
                              data-selected={idx === selectedIndex}
                              selected={idx === selectedIndex}
                              onClick={() => navigateToItem({ type: 'subscription', data: sub })}
                              sx={{
                                mx: 1.5,
                                my: 0.25,
                                borderRadius: 1.5,
                                py: 1,
                                '&.Mui-selected': {
                                  bgcolor: alpha(accent, 0.08),
                                  '&:hover': { bgcolor: alpha(accent, 0.12) },
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.25,
                                  width: '100%',
                                  minWidth: 0,
                                }}
                              >
                                {sub.rxNumber && (
                                  <Typography variant="body2" fontWeight={700} noWrap sx={{ minWidth: 110 }}>
                                    Rx {sub.rxNumber}
                                  </Typography>
                                )}
                                <Typography variant="body2" noWrap sx={{ minWidth: 200, color: 'text.primary' }}>
                                  {sub.medicationName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {sub.patientName}
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                {crossPharmacy && (
                                  <PharmacyBadge
                                    pharmacyId={sub.pharmacyId}
                                    pharmacyName={sub.pharmacyName}
                                  />
                                )}
                                <Chip
                                  label={sub.subscriptionStatus || 'active'}
                                  size="small"
                                  color={
                                    sub.subscriptionStatus === 'active'
                                      ? 'success'
                                      : sub.subscriptionStatus === 'paused'
                                        ? 'warning'
                                        : 'default'
                                  }
                                  variant="outlined"
                                  sx={{ height: 22, fontSize: 11.5, fontWeight: 600 }}
                                />
                                {sub.dueDate && (
                                  <Tooltip title="Refill due date (DD/MM/YYYY)" arrow>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ minWidth: 120, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                                    >
                                      Due {formatDate(sub.dueDate)}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Box>
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </SearchResultColumn>
                  )}
                </Box>
              </Box>
            </Box>
          </Fade>
        )}
      </Box>
    </Dialog>
  );
}
